"use client";

// Шапка портала: одна на публичные и консольные экраны. Гость видит
// «Войти» (документацию читают ДО подключения), залогиненный — выход.
import Link from "next/link";
import { LogIn, LogOut } from "lucide-react";

import { useEffect, useState } from "react";
import { toast } from "sonner";

import { clearTokens, isAuthenticated } from "@/lib/auth";
import { resolveDevLoginUrl } from "@/lib/dev-host";
import { useTranslation } from "@/lib/i18n";

export function DevShellHeader({
  redirect = (url: string) => window.location.replace(url),
}: {
  redirect?: (url: string) => void;
} = {}) {
  const t = useTranslation();
  // Состояние входа живёт в localStorage — на сервере его нет. Читаем
  // ПОСЛЕ монтирования: иначе серверный рендер печатает «Войти», клиент
  // «Выйти», и React роняет дерево hydration-ошибкой (#418).
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    setAuthed(isAuthenticated());
  }, []);

  return (
    <header className="flex h-14 items-center justify-between border-b border-m-divider bg-m-surface px-6">
      <div className="flex items-center gap-6">
        <Link href="/dev" className="font-semibold text-m-text">
          {t("dev.title")}
        </Link>
        <nav className="flex items-center gap-4 text-sm text-m-text-secondary">
          <Link href="/dev/apps" className="hover:text-m-text">{t("dev.nav.apps")}</Link>
          <Link href="/dev/docs" className="hover:text-m-text">{t("dev.nav.docs")}</Link>
        </nav>
      </div>
      {authed === null ? (
        // Первый рендер (и серверный) — нейтральный: место под кнопку.
        <span className="h-4 w-16" aria-hidden />
      ) : authed ? (
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-m-text-secondary hover:text-m-text"
          onClick={() => {
            clearTokens();
            window.location.href = "/login";
          }}
        >
          <LogOut className="h-4 w-4" />
          {t("dev.nav.logout")}
        </button>
      ) : (
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-m-text-secondary hover:text-m-text"
          disabled={signingIn}
          onClick={async () => {
            // Тот же путь, что у авто-редиректа гостя: конфиг сначала.
            setSigningIn(true);
            try {
              redirect(await resolveDevLoginUrl(window.location.origin));
            } catch {
              toast.error(t("dev.login.failed"));
            } finally {
              setSigningIn(false);
            }
          }}
        >
          <LogIn className="h-4 w-4" />
          {t("dev.nav.login")}
        </button>
      )}
    </header>
  );
}
